import { fromBase64, fromHex } from '../bytes/encoding';
import { parseSct, parseSctList, sctFromJson } from './sct';
import type { Sct } from './types';

/**
 * SCTs pasted by hand — the ones a server delivers outside the certificate (TLS extension, OCSP
 * stapling) or saved earlier. Accepts, in any mix of lines:
 *  - base64 or hex of a TLS `SignedCertificateTimestampList`, or of a single TLS SCT
 *  - an add-chain JSON response, an array of them, or this app's JSON bundle (`{ scts: [...] }`)
 */
export function parseSctInput(text: string): Sct[] {
  const t = text.trim();
  if (!t) return [];
  if (t.startsWith('{') || t.startsWith('[')) return fromJson(JSON.parse(t));
  const out: Sct[] = [];
  for (const line of t.split(/\n+/)) {
    const s = line.trim();
    if (!s) continue;
    const bytes = /^[0-9a-fA-F\s:]+$/.test(s) && s.replace(/[\s:]/g, '').length % 2 === 0 ? fromHex(s) : fromBase64(s);
    out.push(...fromTls(bytes));
  }
  return out;
}

function fromTls(bytes: Uint8Array): Sct[] {
  try {
    return parseSctList(bytes);
  } catch {
    return [parseSct(bytes)]; // not a list: a single SCT (throws a ParseError if it is neither)
  }
}

function fromJson(j: unknown): Sct[] {
  if (Array.isArray(j)) return j.flatMap(fromJson);
  if (j && typeof j === 'object') {
    const o = j as Record<string, unknown>;
    if (Array.isArray(o.scts)) return o.scts.flatMap(fromJson); // our bundle
    if (typeof o.sct_tls_base64 === 'string') return [parseSct(fromBase64(o.sct_tls_base64))];
    if (typeof o.sct_list_tls_base64 === 'string') return parseSctList(fromBase64(o.sct_list_tls_base64));
    return [sctFromJson(o)];
  }
  throw new Error('JSON does not look like an SCT, an add-chain response or an SCT bundle');
}
